import { Component, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { UsersService } from 'src/app/users/users.service';
import { SideMenuService } from 'src/app/shared/side-menu/side-menu.service';
import { NocConfigurationService } from './noc-configuration.service';
import { CommonDataService } from 'src/app/shared/services/common-data.service';
import { NotificationMessageService } from 'src/app/shared/notification-message/notification-message.service';
import { ModalComponent } from 'src/app/shared/modal/modal.component';
import { ColumnsConfig, TableConfig, TableData } from 'sct-custom-table/sct-table/projects/sct-table/src/lib/custom-table-interface';
import * as moment from 'moment';
import { CommonService, TimeSource } from 'src/app/shared/services/common.service';

@Component({
	selector: 'app-noc-configurations',
	templateUrl: './noc-configurations.component.html',
	styleUrls: ['./noc-configurations.component.css']
})
export class NocConfigurationsComponent implements OnInit, AfterViewInit {
	@ViewChild('deleteNocConfigModal') deleteNocConfigModal: ModalComponent;

	configurations: any[] = [];
	tableData: TableData[] = [];
	activeTab: string = 'unused'; // 'unused' or 'used'
	hasWriteAccess: boolean = false;
	configToDeleteId: number = null;

	tableConfig: TableConfig = {
		hasExport: true,
		hasPagination: true,
		pageSize: 20,
		fileName: this.translateService.instant('noc_config.title'),
		csvFileHeaderInfo: [
			this.translateService.instant('g.export_time'),
			moment().format('YYYY-MM-DD HH:mm:ss [UTC]Z')
		]
	};

	columnConfig: ColumnsConfig[] = [];

	/** Which time zone each time column is rendered in. See ST-2326. */
	private readonly timeSourceByColumn: Record<string, TimeSource> = {
		created_at: TimeSource.PcTime,
	};

	// Column definitions for unused configurations
	unusedColumnConfig: ColumnsConfig[] = [
		{ key: 'token', name: this.translateService.instant('noc_config.code'), type: 'string', hasSort: true, hasFilter: true },
		{ key: 'meter_number', name: this.translateService.instant('device.meter_number'), type: 'string', hasSort: true, hasFilter: true },
		{ key: 'power_view_sn', name: this.translateService.instant('iotemeter.devices.panel_id'), type: 'string', hasSort: true, hasFilter: true },
		{ key: 'feeder_meter', name: this.translateService.instant('device.feeder_meter'), type: 'string', hasSort: true, hasFilter: true },
		{ key: 'accumulating', name: this.translateService.instant('device.accumulating_subtracting'), type: 'string', hasSort: true, hasFilter: true },
		{ key: 'ct_ratio', name: this.translateService.instant('device.ct_ratio'), type: 'string', hasSort: true, hasFilter: true },
		{ key: 'wiring_mode', name: this.translateService.instant('device.service_type'), type: 'string', hasSort: true, hasFilter: true },
		{ key: 'nominal_voltage', name: this.translateService.instant('device.nominal_voltage'), type: 'string', hasSort: true, hasFilter: true },
		{ key: 'created_at', name: this.translateService.instant('g.created_at'), type: 'date_with_ref', hasSort: true, hasFilter: true }
	];

	// Column definitions for used configurations
	usedColumnConfig: ColumnsConfig[] = [
		{ key: 'token', name: this.translateService.instant('noc_config.code'), type: 'string', hasSort: true, hasFilter: true },
		{ key: 'meter_number', name: this.translateService.instant('device.meter_number'), type: 'string', hasSort: true, hasFilter: true },
		{ key: 'power_view_sn', name: this.translateService.instant('iotemeter.devices.panel_id'), type: 'string', hasSort: true, hasFilter: true },
		{ key: 'feeder_meter', name: this.translateService.instant('device.feeder_meter'), type: 'string', hasSort: true, hasFilter: true },
		{ key: 'accumulating', name: this.translateService.instant('device.accumulating_subtracting'), type: 'string', hasSort: true, hasFilter: true },
		{ key: 'ct_ratio', name: this.translateService.instant('device.ct_ratio'), type: 'string', hasSort: true, hasFilter: true },
		{ key: 'devices', name: this.translateService.instant('noc_config.devices'), type: 'string', hasSort: true, hasFilter: true },
		{ key: 'wiring_mode', name: this.translateService.instant('device.service_type'), type: 'string', hasSort: true, hasFilter: true },
		{ key: 'nominal_voltage', name: this.translateService.instant('device.nominal_voltage'), type: 'string', hasSort: true, hasFilter: true },
		{ key: 'created_at', name: this.translateService.instant('g.created_at'), type: 'date_with_ref', hasSort: true, hasFilter: true }
	];

	// Dropdowns for display - converted from CommonDataService arrays to lookup objects
	wiringModeOptions: any = {};
	accumulatingOptions: any = {};
	nominalVoltageOptions: any = {};
	ctRatioOptions: any = {};

	constructor(
		private translateService: TranslateService,
		private usersService: UsersService,
		private router: Router,
		private nocConfigService: NocConfigurationService,
		private sideMenuService: SideMenuService,
		private commonDataService: CommonDataService,
		private notificationMessage: NotificationMessageService,
		private commonService: CommonService,
	) {
		// Convert arrays from CommonDataService to lookup objects
		this.commonDataService.wiringModeOptions.forEach(option => {
			this.wiringModeOptions[option.value] = option.label;
		});

		this.commonDataService.accumulatingOptions.forEach(option => {
			this.accumulatingOptions[option.value] = option.label;
		});

		this.commonDataService.nominalVoltageOptions.forEach(option => {
			this.nominalVoltageOptions[option.value] = option.label;
		});

		const ctRatioCombinedOptions = [...this.commonDataService.ctRatioOptions, ...this.commonDataService.ctRatioRogowskiType];
		ctRatioCombinedOptions.forEach(option => {
			this.ctRatioOptions[option.value] = option.label;
		});
	}

	ngOnInit() {
		// updateColumnConfig() only ever spreads these two arrays, so the same column objects
		// are reused on every tab switch and one pass here covers both tabs.
		this.commonService.applyTimeSourceTooltips(this.unusedColumnConfig, this.timeSourceByColumn);
		this.commonService.applyTimeSourceTooltips(this.usedColumnConfig, this.timeSourceByColumn);

		if (!this.usersService.hasAccessPermission(null, 'noc'))
			this.router.navigate(['/unauthorized']);

		this.hasWriteAccess = this.usersService.hasAccessPermission(null, 'noc', 'write');

		this.sideMenuService.hide();

		this.updateColumnConfig();

		this.getAllConfigurations();
	}

	ngAfterViewInit() {
		this.deleteNocConfigModal.onClose.subscribe((confirmed) => {
			if (confirmed) {
				this.confirmDeleteConfiguration();
			} else {
				this.configToDeleteId = null;
			}
		});
	}

	getAllConfigurations() {
		this.nocConfigService.getAllNocConfigurations().subscribe((response: any) => {
			if (response) {
				this.configurations = response || [];
				this.updateTableData();
			}
		});
	}

	updateTableData() {
		const filteredConfigs = this.activeTab === 'unused'
			? this.configurations.filter(c => !c.mac_address)
			: this.configurations.filter(c => c.mac_address);

		this.tableData = filteredConfigs.map(config => this.transformConfigToTableData(config));
	}

	transformConfigToTableData(config: any): TableData {
		const configData = config.config || {};

		const rowData: TableData = {
			record: config,
			token: { value: config.token || '' },
			meter_number: { value: configData.meter_number || '' },
			power_view_sn: { value: configData.power_view_sn || '' },
			feeder_meter: { value: configData.is_feeder_meter === 1 ? 'Yes' : 'No' },
			accumulating: { value: this.accumulatingOptions[configData.is_meter_subtracting] || configData.is_meter_subtracting },
			ct_ratio: { value: this.ctRatioOptions[configData.ct_ratio] || configData.ct_ratio },
			wiring_mode: { value: this.wiringModeOptions[configData.wiring_mode] || configData.wiring_mode },
			nominal_voltage: { value: this.nominalVoltageOptions[configData.nominal_voltage] || configData.nominal_voltage },
			created_at: { value: config.created_at ? moment.unix(config.created_at).format('MM/DD/YYYY HH:mm') : '' }
		};

		// Add devices column for used configurations
		if (this.activeTab === 'used') {
			rowData.devices = { value: config.mac_address || '' };
		}

		// Add edit/delete buttons for unused configurations with write access
		if (this.activeTab === 'unused' && this.hasWriteAccess) {
			rowData.edit = {
				icon: 'edit',
				iconColor: '#007bff',
				action: () => this.editConfiguration(config.id)
			};

			rowData.delete = {
				icon: 'trash',
				iconColor: '#dc3545',
				action: () => this.deleteConfiguration(config.id)
			};
		}

		return rowData;
	}

	switchTab(tab: string) {
		this.activeTab = tab;
		this.updateColumnConfig();
		this.updateTableData();
	}

	updateColumnConfig() {
		if (this.activeTab === 'unused') {
			this.columnConfig = [...this.unusedColumnConfig];

			// Add edit/delete columns if has write access
			if (this.hasWriteAccess) {
				this.columnConfig.push(
					{ key: 'edit', name: this.translateService.instant('g.edit'), type: 'icon', hasSort: false, hasFilter: false },
					{ key: 'delete', name: this.translateService.instant('g.delete'), type: 'icon', hasSort: false, hasFilter: false }
				);
			}
		} else {
			this.columnConfig = [...this.usedColumnConfig];
		}
	}

	addConfiguration() {
		this.router.navigate(['/noc-tools/noc-configurations/add']);
	}

	editConfiguration(id: number) {
		this.router.navigate(['/noc-tools/noc-configurations/edit', id]);
	}

	deleteConfiguration(id: number) {
		this.configToDeleteId = id;
		this.deleteNocConfigModal.show();
	}

	private confirmDeleteConfiguration() {
		if (this.configToDeleteId) {
			this.nocConfigService.deleteNocConfiguration(this.configToDeleteId).subscribe((response: any) => {
				if (response === 1) {
					this.notificationMessage.setMessage('g.deleted_successfully', { type: 'success', clearOnXTimeNavigate: 1 });
					this.getAllConfigurations();
				} else {
					this.notificationMessage.setMessage('g.failed', { clearOnXTimeNavigate: 1 });
				}
				this.configToDeleteId = null;
			});
		}
	}

	manageRecipients() {
		this.router.navigate(['/noc-tools/noc-configurations/emails']);
	}
}
